<template>
  <div class="entra-page">
    <EntraPageHeader
      title="Identity Security"
      :tenantDisplayName="tenantName"
      :primaryDomain="primaryDomain"
      :regionCode="regionCode"
      :tenantStatus="tenantStatus"
      :freshnessState="freshnessState"
      :lastCollection="lastCollection"
      :dataCompleteness="dataCompleteness"
      :capabilities="capabilities"
      @refresh="loadData"
      @export="handleExport"
    />

    <div class="page-body">
      <div v-if="error" class="error-banner">{{ error }}</div>

      <div class="panels-row">
        <div class="panel-card">
          <div class="panel-header">
            <div class="panel-icon-wrapper panel-icon-auth">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5" width="18" height="18">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
              </svg>
            </div>
            <h3>Authentication Posture</h3>
          </div>

          <div v-if="authState === 'loading'" class="loading-block">Loading...</div>
          <div v-else class="metrics-list">
            <div class="metric-row">
              <span class="metric-label">MFA Registered</span>
              <span class="metric-value" :class="auth.mfaRegisteredPercentage >= 80 ? 'val-ok' : auth.mfaRegisteredPercentage >= 40 ? 'val-warn' : 'val-bad'">{{ formatPercentage(auth.mfaRegisteredPercentage) }}</span>
            </div>
            <div class="metric-row">
              <span class="metric-label">MFA Capable</span>
              <span class="metric-value" :class="auth.mfaCapablePercentage >= 80 ? 'val-ok' : auth.mfaCapablePercentage >= 40 ? 'val-warn' : 'val-bad'">{{ formatPercentage(auth.mfaCapablePercentage) }}</span>
            </div>
            <div class="metric-row">
              <span class="metric-label">SSPR Registered</span>
              <span class="metric-value" :class="auth.ssprRegisteredPercentage >= 80 ? 'val-ok' : 'val-warn'">{{ formatPercentage(auth.ssprRegisteredPercentage) }}</span>
            </div>
            <div class="metric-row">
              <span class="metric-label">Not MFA Capable</span>
              <span class="metric-value val-count">{{ auth.enabledMembersNotMfaCapable }} / {{ auth.totalEnabledMembers }}</span>
            </div>
          </div>
        </div>

        <div class="panel-card">
          <div class="panel-header">
            <div class="panel-icon-wrapper panel-icon-ca">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5" width="18" height="18">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
              </svg>
            </div>
            <h3>Conditional Access</h3>
          </div>

          <div class="metrics-list">
            <div class="metric-row">
              <span class="metric-label">Total Policies</span>
              <span class="metric-value">{{ caSummary.caPoliciesTotal }}</span>
            </div>
            <div class="metric-row">
              <span class="metric-label">Enabled</span>
              <span class="metric-value" :class="caSummary.caPoliciesEnabled > 0 ? 'val-ok' : 'val-warn'">{{ caSummary.caPoliciesEnabled }}</span>
            </div>
            <div class="metric-row">
              <span class="metric-label">Open Findings</span>
              <span class="metric-value" :class="caSummary.totalOpenFindings === 0 ? 'val-ok' : 'val-bad'">{{ caSummary.totalOpenFindings }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="panels-row">
        <div class="panel-card">
          <div class="panel-header">
            <div class="panel-icon-wrapper panel-icon-priv">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5" width="18" height="18">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
              </svg>
            </div>
            <h3>Privileged Access</h3>
          </div>

          <div v-if="privState === 'loading'" class="loading-block">Loading...</div>
          <div v-else class="metrics-list">
            <div class="metric-row">
              <span class="metric-label">Active Privileged Users</span>
              <span class="metric-value">{{ priv.activePrivilegedUsers }}</span>
            </div>
            <div class="metric-row">
              <span class="metric-label">Global Administrators</span>
              <span class="metric-value" :class="priv.globalAdmins > 0 ? 'val-warn' : 'val-ok'">{{ priv.globalAdmins }}</span>
            </div>
            <div class="metric-row">
              <span class="metric-label">Permanent Assignments</span>
              <span class="metric-value" :class="priv.permanentAssignments > 0 ? 'val-warn' : 'val-ok'">{{ priv.permanentAssignments }}</span>
            </div>
            <div class="metric-row">
              <span class="metric-label">Privileged SPs</span>
              <span class="metric-value">{{ priv.privilegedServicePrincipals }}</span>
            </div>
            <div class="metric-row">
              <span class="metric-label">Guests with Roles</span>
              <span class="metric-value" :class="priv.guestsWithRoles > 0 ? 'val-bad' : 'val-ok'">{{ priv.guestsWithRoles }}</span>
            </div>
            <div class="metric-row">
              <span class="metric-label">Disabled with Roles</span>
              <span class="metric-value" :class="priv.disabledUsersWithRoles > 0 ? 'val-bad' : 'val-ok'">{{ priv.disabledUsersWithRoles }}</span>
            </div>
          </div>
        </div>

        <div class="panel-card">
          <div class="panel-header">
            <div class="panel-icon-wrapper panel-icon-risk" :class="risk.totalRisky > 0 ? 'panel-icon-risk-active' : ''">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5" width="18" height="18">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"/>
              </svg>
            </div>
            <h3>Identity Risk</h3>
          </div>

          <div v-if="riskState === 'loading'" class="loading-block">Loading...</div>
          <div v-else-if="!riskAvailable" class="empty-block">Identity protection requires Entra ID P2 licensing.</div>
          <div v-else class="metrics-list">
            <div class="metric-row">
              <span class="metric-label">Total Risky Users</span>
              <span class="metric-value" :class="risk.totalRisky > 0 ? 'val-bad' : 'val-ok'">{{ risk.totalRisky }}</span>
            </div>
            <div class="metric-row">
              <span class="metric-label">High Risk</span>
              <span class="metric-value val-bad">{{ risk.highRisk }}</span>
            </div>
            <div class="metric-row">
              <span class="metric-label">Medium Risk</span>
              <span class="metric-value val-warn">{{ risk.mediumRisk }}</span>
            </div>
            <div class="metric-row">
              <span class="metric-label">Low Risk</span>
              <span class="metric-value">{{ risk.lowRisk }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="health-score-card">
        <div class="health-header">
          <div class="health-score-circle" :style="{ '--score': healthScore.score || 0 }">
            <span class="health-score-number">{{ healthScore.score || '--' }}</span>
            <span class="health-score-max">/100</span>
          </div>
          <div class="health-title-group">
            <h2>Cyanic Identity Health Score</h2>
            <span class="health-completeness">Data completeness: {{ formatPercentage(healthScore.dataCompleteness) }}</span>
          </div>
        </div>
        <div class="health-domains">
          <div v-for="domain in healthScoreDomains" :key="domain.key" class="health-domain">
            <span class="domain-name">{{ domain.label }}</span>
            <div class="domain-track">
              <div class="domain-fill" :style="{ width: domain.score + '%', backgroundColor: domainColor(domain.score) }"></div>
            </div>
            <span class="domain-pct">{{ domain.score }}%</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import EntraPageHeader from '@/components/entra/common/EntraPageHeader.vue';
import { getEntraAuthenticationPosture, getEntraPrivilegedAccess, getEntraRiskyUsers, getEntraHealthScore, getEntraTenantSummary, getEntraTenantCapabilities, getEntraSecuritySummary } from '@/services/entra-api.js';
import { formatPercentage, getCapabilityStateLabel } from '@/services/entra-utils.js';

export default {
  name: 'EntraSecurity',
  components: { EntraPageHeader },
  data: function() {
    return {
      tenantId: '', tenantName: '', primaryDomain: '', regionCode: '', tenantStatus: '',
      freshnessState: 'fresh', lastCollection: '', dataCompleteness: null, capabilities: [],
      auth: { mfaRegisteredPercentage: null, mfaCapablePercentage: null, ssprRegisteredPercentage: null, enabledMembersNotMfaCapable: 0, totalEnabledMembers: 0 },
      authState: 'loading',
      priv: { activePrivilegedUsers: 0, globalAdmins: 0, permanentAssignments: 0, privilegedServicePrincipals: 0, guestsWithRoles: 0, disabledUsersWithRoles: 0 },
      privState: 'loading',
      risk: { totalRisky: 0, highRisk: 0, mediumRisk: 0, lowRisk: 0 }, riskState: 'loading',
      riskAvailable: true,
      caSummary: { caPoliciesTotal: 0, caPoliciesEnabled: 0, totalOpenFindings: 0 }, summaryState: 'loading',
      healthScore: { score: null, dataCompleteness: null },
      healthScoreDomains: [],
      error: ''
    };
  },
  methods: {
    formatPercentage: formatPercentage,
    domainColor: function(score) { if (score >= 80) return '#16a34a'; if (score >= 60) return '#ca8a04'; return '#dc2626'; },
    handleExport: function() {},
    loadData: function() {
      var self = this;
      this.tenantId = this.$route.params.tenantId || '';
      if (!this.tenantId) {
        this.error = 'Please select a tenant from the Portfolio page first.';
        this.authState = 'empty'; this.privState = 'empty'; this.riskState = 'empty';
        return;
      }
      getEntraTenantSummary(this.tenantId).then(function(data) {
        var t = data.tenant || data;
        self.tenantName = t.displayName || ''; self.primaryDomain = t.primaryDomain || '';
        self.regionCode = t.regionCode || ''; self.tenantStatus = t.status || '';
        self.lastCollection = t.lastSuccessfulCollectionAt || ''; self.dataCompleteness = t.dataCompleteness || null;
      }).catch(function() {});
      getEntraTenantCapabilities(this.tenantId).then(function(data) {
        self.capabilities = (data || []).map(function(c) {
          return { key: c.capabilityKey, state: c.state, stateLabel: getCapabilityStateLabel(c.state) };
        });
      }).catch(function() {});
      getEntraSecuritySummary(this.tenantId).then(function(data) {
        self.caSummary.caPoliciesTotal = data.caPoliciesTotal || 0;
        self.caSummary.caPoliciesEnabled = data.caPoliciesEnabled || 0;
        self.caSummary.totalOpenFindings = data.totalOpenFindings || 0;
        self.summaryState = 'loaded';
      }).catch(function() {});
      getEntraAuthenticationPosture(this.tenantId).then(function(data) {
        Object.assign(self.auth, data); self.authState = 'loaded';
      }).catch(function(err) { self.error = err.message; self.authState = 'error'; });
      getEntraPrivilegedAccess(this.tenantId).then(function(data) {
        Object.assign(self.priv, data); self.privState = 'loaded';
      }).catch(function() { self.privState = 'error'; });
      getEntraRiskyUsers(this.tenantId).then(function(data) {
        Object.assign(self.risk, data); self.riskState = 'loaded';
      }).catch(function() { self.riskAvailable = false; self.riskState = 'unavailable'; });
      getEntraHealthScore(this.tenantId).then(function(data) {
        self.healthScore.score = data.score;
        self.healthScore.dataCompleteness = data.dataCompleteness;
        self.healthScoreDomains = (data.domains || []).map(function(d) {
          return { key: d.key, label: d.label, score: d.score };
        });
      }).catch(function() {});
    }
  },
  mounted: function() { this.loadData(); },
  watch: { '$route.params.tenantId': 'loadData' }
};
</script>

<style scoped>
.entra-page { min-height: 100%; background: #F0F9FF; }
.page-body { padding: 20px 24px; }

.error-banner { padding: 12px 16px; background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px; color: #991b1b; font-size: 0.8125rem; margin-bottom: 20px; }

.panels-row { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 14px; }

.panel-card {
  background: #ffffff; border: 1px solid #e2e8f0; border-radius: 10px;
  overflow: hidden;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.panel-card:hover { border-color: #7dd3fc; box-shadow: 0 2px 8px rgba(3,105,161,0.06); }

.panel-header {
  display: flex; align-items: center; gap: 10px;
  padding: 14px 16px; border-bottom: 1px solid #f1f5f9;
}
.panel-header h3 { margin: 0; font-size: 0.8125rem; font-weight: 600; color: #0C4A6E; }

.panel-icon-wrapper { width: 32px; height: 32px; border-radius: 8px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.panel-icon-auth { background: #dbeafe; color: #0369A1; }
.panel-icon-ca { background: #fef3c7; color: #92400e; }
.panel-icon-priv { background: #ede9fe; color: #6d28d9; }
.panel-icon-risk { background: #f1f5f9; color: #94a3b8; }
.panel-icon-risk-active { background: #fee2e2; color: #dc2626; }

.loading-block { padding: 32px; text-align: center; color: #94a3b8; font-size: 0.8125rem; }
.empty-block { padding: 32px; text-align: center; color: #94a3b8; font-size: 0.75rem; }

.metrics-list { padding: 8px 0; }
.metric-row {
  display: flex; justify-content: space-between; align-items: center;
  padding: 8px 16px; transition: background 0.1s;
}
.metric-row:hover { background: #F0F9FF; }
.metric-label { font-size: 0.8125rem; color: #0C4A6E; font-weight: 500; }
.metric-value { font-size: 0.875rem; font-weight: 700; color: #0C4A6E; }

.val-ok { color: #047857; }
.val-warn { color: #92400e; }
.val-bad { color: #dc2626; }
.val-count { font-weight: 500; color: #64748b; font-size: 0.8125rem; }

.health-score-card {
  background: #ffffff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 24px;
}
.health-header { display: flex; align-items: center; gap: 20px; margin-bottom: 20px; }

.health-score-circle {
  width: 80px; height: 80px; border-radius: 50%;
  background: conic-gradient(#0369A1 calc(var(--score, 0) * 1%), #e2e8f0 0);
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.health-score-number { font-size: 1.5rem; font-weight: 700; color: #0369A1; line-height: 1; }
.health-score-max { font-size: 0.6875rem; color: #94a3b8; }

.health-title-group { display: flex; flex-direction: column; gap: 2px; }
.health-title-group h2 { margin: 0; font-size: 1rem; font-weight: 600; color: #0C4A6E; }
.health-completeness { font-size: 0.6875rem; color: #94a3b8; }

.health-domains { display: flex; flex-direction: column; gap: 10px; }
.health-domain { display: flex; align-items: center; gap: 12px; }
.domain-name { width: 160px; font-size: 0.75rem; color: #475569; text-align: right; flex-shrink: 0; }
.domain-track { flex: 1; height: 8px; background: #f1f5f9; border-radius: 4px; overflow: hidden; }
.domain-fill { height: 100%; border-radius: 4px; transition: width 0.4s ease; }
.domain-pct { width: 36px; font-size: 0.75rem; font-weight: 600; color: #334155; text-align: right; flex-shrink: 0; }

@media (max-width: 768px) {
  .panels-row { grid-template-columns: 1fr; }
  .health-header { flex-direction: column; text-align: center; }
  .domain-name { width: 100px; font-size: 0.6875rem; }
}
</style>
